import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Globe, Users, MailCheck, Inbox, type LucideProps } from 'lucide-react'
import { api, relTime, statusBadge, type Me } from '../lib/api'
import { BarList, Donut, StackedBars } from '../components/Chart'

const ki: LucideProps = { size: 17, strokeWidth: 2 }

/*
 * The operator (superadmin) console.
 *
 * Cross-tenant, superadmin-only. The server enforces the same, so the visuals here are
 * about legibility at a glance, not access control.
 */

const PLAN_COLOURS: Record<string, string> = {
  free: '#6b7688', sandbox: '#6b7688', starter: '#35d6a4',
  pro: '#3b6ef6', enterprise: '#b98bff',
}
const STATUS_COLOURS: Record<string, string> = {
  Active: 'var(--success)', Sandbox: 'var(--info)', PendingReview: 'var(--warning)',
  Suspended: 'var(--danger)', Closed: 'var(--danger)',
}

function KpiCard({ label, value, sub, icon, tone = 'brand' }: {
  label: string; value: number | string; sub?: string
  icon?: React.ReactNode; tone?: 'brand' | 'ok' | 'warn' | 'danger' | 'info'
}) {
  return (
    <div className="kpi">
      {icon && <span className={`kpi-ico ${tone}`}>{icon}</span>}
      <div className="kpi-body">
        <span className="kpi-value">{typeof value === 'number' ? value.toLocaleString() : value}</span>
        <span className="kpi-label">{label}</span>
        {sub && <span className="kpi-sub">{sub}</span>}
      </div>
    </div>
  )
}

// ==================================================================== overview

export function AdminOverview({ me }: { me: Me }) {
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'overview'], queryFn: api.admin.overview })

  if (isLoading) return <div className="skeleton" style={{ height: 360 }} />
  const t = data

  const labels = (t?.trend ?? []).map((d: any) =>
    new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))

  const planItems = (t?.tenants?.by_plan ?? []).map((p: any) => ({
    label: p.plan, value: p.count, colour: PLAN_COLOURS[p.plan] ?? 'var(--brand-500)',
  }))
  const statusItems = (t?.tenants?.by_status ?? []).map((p: any) => ({
    label: p.status, value: p.count, colour: STATUS_COLOURS[p.status] ?? 'var(--brand-500)',
  }))

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="op-badge">Operator</span>
          <h1 style={{ marginTop: 8 }}>Platform console</h1>
          <p className="lede" style={{ marginTop: 4 }}>{me.user.email} · every tenant, every domain, at a glance.</p>
        </div>
        <Link className="btn-solid" to="/admin/tenants">Manage tenants →</Link>
      </div>

      <div className="kpis">
        <KpiCard label="Tenants" value={t?.tenants?.total ?? 0} sub="total accounts" icon={<Building2 {...ki} />} tone="brand" />
        <KpiCard label="Domains" value={t?.counts?.domains ?? 0} sub={`${t?.counts?.verified_domains ?? 0} verified`} icon={<Globe {...ki} />} tone="info" />
        <KpiCard label="Users" value={t?.counts?.users ?? 0} sub={`${t?.counts?.api_keys ?? 0} active keys`} icon={<Users {...ki} />} tone="warn" />
        <KpiCard label="Delivered today" value={t?.today?.delivered ?? 0} sub={`${t?.today?.bounced ?? 0} bounced`} icon={<MailCheck {...ki} />} tone="ok" />
        <KpiCard label="Inbound stored" value={t?.counts?.inbound_messages ?? 0} sub="received messages" icon={<Inbox {...ki} />} tone="info" />
      </div>

      <div className="admin-grid">
        <SignupGateCard />
        <AntiSpamCard />
        <BackupCard />
        <BannedIpsCard />
        <PendingApprovals />
      </div>

      <PlansManager />

      <div className="admin-grid">
        <div className="panel">
          <div className="panel-head"><h2>Delivery — 30 days</h2></div>
          <StackedBars
            labels={labels}
            series={[
              { key: 'delivered', label: 'Delivered', colour: 'var(--success)', values: (t?.trend ?? []).map((d: any) => d.delivered) },
              { key: 'deferred', label: 'Deferred', colour: 'var(--warning)', values: (t?.trend ?? []).map((d: any) => d.deferred) },
              { key: 'bounced', label: 'Bounced', colour: 'var(--danger)', values: (t?.trend ?? []).map((d: any) => d.bounced) },
            ]}
          />
        </div>
        <div className="panel">
          <div className="panel-head"><h2>Tenants by plan</h2></div>
          <Donut items={planItems.length ? planItems : [{ label: 'none', value: 0, colour: 'var(--surface-3)' }]} />
        </div>
      </div>

      <div className="admin-grid">
        <div className="panel">
          <div className="panel-head"><h2>Accounts by status</h2></div>
          <BarList items={statusItems} />
        </div>
        <div className="panel">
          <div className="panel-head"><h2>Today</h2></div>
          <table className="kv">
            <tbody>
              <tr><td>Accepted</td><td><strong>{(t?.today?.accepted ?? 0).toLocaleString()}</strong></td></tr>
              <tr><td>Delivered</td><td className="ok-text">{(t?.today?.delivered ?? 0).toLocaleString()}</td></tr>
              <tr><td>Bounced</td><td className="crit">{(t?.today?.bounced ?? 0).toLocaleString()}</td></tr>
              <tr><td>Complaints</td><td className="crit">{(t?.today?.complained ?? 0).toLocaleString()}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ---- plans manager (owner edits limits; system enforces them live) --------------

function PlansManager() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['admin', 'plans'], queryFn: api.admin.plans })
  const rows = data?.data ?? []
  return (
    <div className="panel">
      <div className="panel-head"><h2>Plans &amp; limits</h2></div>
      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <th>Plan</th><th className="num">Daily</th><th className="num">Hourly</th>
              <th className="num">Domains</th><th className="num">Recip/msg</th><th className="num">Inbound days</th>
              <th>Tracking</th><th>Ded. IP</th><th></th>
            </tr>
          </thead>
          <tbody>{rows.map((p: any) => (
            <PlanRow key={p.id} p={p} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'plans'] })} />
          ))}</tbody>
        </table>
      </div>
      <p className="small muted" style={{ margin: '10px 2px 0' }}>
        Changes apply live — every send, domain add and key create reads these limits, so the
        platform behaves according to whatever you set here.
      </p>
    </div>
  )
}

function PlanRow({ p, onSaved }: { p: any; onSaved: () => void }) {
  const [f, setF] = useState({
    daily: p.daily, hourly: p.hourly, max_domains: p.max_domains, max_recipients: p.max_recipients,
    inbound_retention_days: p.inbound_retention_days, tracking_allowed: p.tracking_allowed, dedicated_ip: p.dedicated_ip,
  })
  const save = useMutation({ mutationFn: () => api.admin.updatePlan(p.id, f), onSuccess: onSaved })
  const num = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF(s => ({ ...s, [k]: Number(e.target.value) }))
  const chk = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF(s => ({ ...s, [k]: e.target.checked }))
  const inp = { width: 82 } as const
  return (
    <tr>
      <td><strong>{p.name}</strong>{p.is_free && <span className="badge neutral" style={{ marginLeft: 6 }}>free</span>}</td>
      <td className="num"><input type="number" value={f.daily} onChange={num('daily')} style={inp} /></td>
      <td className="num"><input type="number" value={f.hourly} onChange={num('hourly')} style={inp} /></td>
      <td className="num"><input type="number" value={f.max_domains} onChange={num('max_domains')} style={inp} /></td>
      <td className="num"><input type="number" value={f.max_recipients} onChange={num('max_recipients')} style={inp} /></td>
      <td className="num"><input type="number" value={f.inbound_retention_days} onChange={num('inbound_retention_days')} style={inp} /></td>
      <td><input type="checkbox" checked={f.tracking_allowed} onChange={chk('tracking_allowed')} /></td>
      <td><input type="checkbox" checked={f.dedicated_ip} onChange={chk('dedicated_ip')} /></td>
      <td className="right"><button className="small" onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending ? '…' : save.isSuccess ? 'Saved ✓' : 'Save'}</button></td>
    </tr>
  )
}

// ---- signup gate + pending approvals -------------------------------------------

function SignupGateCard() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['admin', 'settings'], queryFn: api.admin.settings })
  const update = useMutation({
    mutationFn: (v: boolean) => api.admin.updateSettings({ require_owner_approval: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  })
  const on = data?.require_owner_approval ?? true

  return (
    <div className="panel">
      <div className="panel-head"><h2>New-signup gate</h2></div>
      <label className="row" style={{ gap: 12, alignItems: 'flex-start', cursor: 'pointer', padding: '4px 2px' }}>
        <input type="checkbox" checked={on} disabled={update.isPending}
               onChange={e => update.mutate(e.target.checked)}
               style={{ width: 18, height: 18, marginTop: 2 }} />
        <span>
          <strong style={{ display: 'block' }}>Require owner approval for new signups</strong>
          <span className="small muted">
            {on
              ? 'New accounts verify their email, then wait for you to approve them before they can add domains or send.'
              : 'New accounts open into the sandbox as soon as they verify their email — no manual approval.'}
          </span>
        </span>
      </label>
      <p className="small muted" style={{ margin: '10px 2px 0' }}>
        Email verification is always required, whichever mode is on.
      </p>
    </div>
  )
}

/** Database backup: shows when the last backup ran and lets the operator take one on demand.
 *  Daily automatic backups run in BackupWorker; a failure emails the operator. Superadmin only. */
function BackupCard() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['admin', 'backup-status'], queryFn: api.admin.backupStatus })
  const run = useMutation({
    mutationFn: () => api.admin.runBackup(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'backup-status'] }),
  })
  const last = data?.last_backup_at

  return (
    <div className="panel">
      <div className="panel-head"><h2>Database backup</h2></div>
      <table className="kv" style={{ marginBottom: 12 }}>
        <tbody>
          <tr>
            <td>Last backup</td>
            <td>{last
              ? <>{relTime(last)} <span className="muted">· {data?.size_mb?.toFixed(1)} MB</span></>
              : <span className="crit">never</span>}</td>
          </tr>
          {data?.file && <tr><td>File</td><td className="mono small truncate">{data.file}</td></tr>}
          <tr><td>Freshness</td><td>{data?.stale
            ? <span className="badge critical">Stale</span>
            : <span className="badge ok">Fresh</span>}</td></tr>
        </tbody>
      </table>
      <button onClick={() => run.mutate()} disabled={run.isPending}>
        {run.isPending ? 'Backing up…' : 'Back up now'}
      </button>
      {run.isSuccess && (
        <div className="alert info" style={{ marginTop: 10 }}>
          Backup complete — {run.data.file} ({run.data.size_mb?.toFixed(1)} MB).
        </div>
      )}
      {run.isError && (
        <div className="alert error" style={{ marginTop: 10 }}>
          {(run.error as { message?: string })?.message ?? 'Backup failed.'}
        </div>
      )}
      <p className="small muted" style={{ margin: '10px 2px 0' }}>
        Runs automatically once a day. Backups are written on the SQL Server and pulled off-site by
        the local download script; a failure emails the operator.
      </p>
    </div>
  )
}

/** Banned SSH IPs (fail2ban), read via the root bridge. View + manual unban only — nothing here
 *  bans anyone automatically; fail2ban's own SSH jail does the banning at the OS level. */
function BannedIpsCard() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['admin', 'banned-ips'], queryFn: api.admin.bannedIps, refetchInterval: 30000 })
  const unban = useMutation({
    mutationFn: (ip: string) => api.admin.unbanIp(ip),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'banned-ips'] }),
  })
  const banned = data?.banned ?? []

  return (
    <div className="panel">
      <div className="panel-head"><h2>Banned IPs (SSH)</h2></div>
      {data && !data.bridge && (
        <div className="alert warn" style={{ marginBottom: 10 }}>
          The fail2ban bridge has not published a list yet.
        </div>
      )}
      {banned.length === 0
        ? <div className="hint">No IPs are currently banned.</div>
        : (
          <table className="data">
            <tbody>
              {banned.map(ip => (
                <tr key={ip}>
                  <td className="mono small">{ip}</td>
                  <td className="right">
                    <button className="secondary small"
                            disabled={unban.isPending}
                            onClick={() => unban.mutate(ip)}>
                      {unban.isPending && unban.variables === ip ? 'Unbanning…' : 'Unban'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      {unban.isSuccess && <div className="alert info" style={{ marginTop: 10 }}>{unban.data.note}</div>}
      {unban.isError && <div className="alert error" style={{ marginTop: 10 }}>{(unban.error as { message?: string })?.message ?? 'Could not unban.'}</div>}
      <p className="small muted" style={{ margin: '10px 2px 0' }}>
        fail2ban auto-bans SSH brute-force at the OS level. This panel is view + manual unban only —
        nothing is blocked automatically from here. Unbans apply within a minute.
      </p>
    </div>
  )
}

/** Anti-spam thresholds. Default is alert-only — a breach emails the operator without
 *  suspending anyone. Turning auto-suspend on makes a breach stop the tenant's sending too. */
function AntiSpamCard() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['admin', 'settings'], queryFn: api.admin.settings })
  const update = useMutation({
    mutationFn: (b: any) => api.admin.updateSettings(b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  })
  const [c, setC] = useState<string>('')
  const [h, setH] = useState<string>('')
  const [v, setV] = useState<string>('')
  if (!data) return <div className="panel"><div className="skeleton" style={{ height: 120 }} /></div>
  const autoSuspend = data.auto_suspend_enabled

  const sesRelay = data.ses_relay_enabled

  return (
    <>
    {/* Outbound routing. Deliberately its own panel and placed above anti-spam:
        it decides whether customer mail leaves through our own MTA or a third
        party, which is the single most consequential switch on this screen. */}
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-head"><h2>Outbound routing</h2></div>
      <p className="small muted" style={{ marginTop: 0 }}>
        AWS has not unblocked outbound port 25 on this account, so mail cannot be delivered
        straight to recipient mail servers from this host. Relaying through Amazon SES works
        around that. It is meant to be temporary — turn it off the moment port 25 is available
        and delivery goes direct again, with no deploy and no restart.
      </p>
      <label className="row" style={{ gap: 12, alignItems: 'flex-start', cursor: 'pointer', padding: '4px 2px' }}>
        <input type="checkbox" checked={sesRelay} disabled={update.isPending}
               onChange={e => update.mutate({ ses_relay_enabled: e.target.checked })}
               style={{ width: 18, height: 18, marginTop: 2 }} />
        <span>
          <strong style={{ display: 'block' }}>Relay outbound mail through Amazon SES</strong>
          <span className="small muted">
            {sesRelay
              ? 'On — messages carry a routing header and Postfix hands them to SES on 587. Per-message DKIM, VERP return paths and the audit trail are unchanged; only the last hop differs.'
              : 'Off — Postfix delivers directly to each recipient’s MX on port 25. This is the intended architecture and requires port 25 to be open outbound.'}
          </span>
        </span>
      </label>
      {!sesRelay && (
        <p className="small" style={{ marginTop: 10, color: 'var(--warn, #8a5a12)' }}>
          Direct delivery needs outbound port 25. If it is still blocked, messages will queue
          and eventually fail rather than being delivered.
        </p>
      )}
    </div>

    <div className="panel">
      <div className="panel-head"><h2>Anti-spam protection</h2></div>
      <p className="small muted" style={{ marginTop: 0 }}>
        Every 15 minutes each tenant's 24-hour complaint and hard-bounce rate is checked. A breach
        always emails the operator; it only suspends the tenant if auto-suspend is on.
      </p>
      <label className="row" style={{ gap: 12, alignItems: 'flex-start', cursor: 'pointer', padding: '4px 2px' }}>
        <input type="checkbox" checked={autoSuspend} disabled={update.isPending}
               onChange={e => update.mutate({ auto_suspend_enabled: e.target.checked })}
               style={{ width: 18, height: 18, marginTop: 2 }} />
        <span>
          <strong style={{ display: 'block' }}>Auto-suspend on breach</strong>
          <span className="small muted">
            {autoSuspend
              ? 'A tenant over the threshold is suspended automatically (sending stops) and you are alerted.'
              : 'Alert-only: you get an email on a breach but no one is suspended — you decide.'}
          </span>
        </span>
      </label>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Complaint % limit</label>
          <input type="number" step="0.01" style={{ width: 120 }}
                 placeholder={String(data.complaint_rate_threshold)} value={c} onChange={e => setC(e.target.value)}
                 onBlur={() => { if (c) { update.mutate({ complaint_rate_threshold: Number(c) }); setC('') } }} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Hard-bounce % limit</label>
          <input type="number" step="0.1" style={{ width: 120 }}
                 placeholder={String(data.hard_bounce_rate_threshold)} value={h} onChange={e => setH(e.target.value)}
                 onBlur={() => { if (h) { update.mutate({ hard_bounce_rate_threshold: Number(h) }); setH('') } }} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Min. volume to judge</label>
          <input type="number" style={{ width: 120 }}
                 placeholder={String(data.anti_spam_min_volume)} value={v} onChange={e => setV(e.target.value)}
                 onBlur={() => { if (v) { update.mutate({ anti_spam_min_volume: Number(v) }); setV('') } }} />
        </div>
      </div>
      <p className="small muted" style={{ margin: '10px 2px 0' }}>
        Alerts go to the ops address. A tenant under the minimum volume is never judged.
      </p>
    </div>
    </>
  )
}

function PendingApprovals() {
  const { data } = useQuery({
    queryKey: ['admin', 'tenants', 'pending'],
    queryFn: () => api.admin.tenants({ status: 'PendingReview', limit: 25 }),
  })
  const rows = data?.data ?? []

  return (
    <div className="panel">
      <div className="panel-head"><h2>Pending approval{rows.length ? ` (${rows.length})` : ''}</h2></div>
      {rows.length === 0 ? (
        <p className="small muted" style={{ padding: '6px 2px' }}>
          Nothing waiting. Verified accounts needing approval show up here.
        </p>
      ) : (
        <table className="kv">
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id}>
                <td>
                  <strong>{r.name}</strong>
                  <span className="small muted" style={{ display: 'block' }}>
                    {r.website || 'no website'} · {new Date(r.created_at).toLocaleDateString()}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <Link className="button small" to={`/admin/verifications/${r.id}`}>Review</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ==================================================================== tenants list

function TenantStatus({ s }: { s: string }) {
  const map: Record<string, string> = {
    Active: 'ok', Sandbox: 'info', PendingReview: 'pending', Suspended: 'critical', Closed: 'critical',
  }
  return <span className={`badge ${map[s] ?? 'info'}`}>{s}</span>
}

export function AdminTenants() {
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tenants', query],
    queryFn: () => api.admin.tenants({ search: query || undefined, limit: 200 }),
  })

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="op-badge">Operator</span>
          <h1 style={{ marginTop: 8 }}>Tenants</h1>
        </div>
        <span className="small muted">{data?.total ?? 0} accounts</span>
      </div>

      <div className="toolbar">
        <form onSubmit={e => { e.preventDefault(); setQuery(search.trim()) }} className="row" style={{ flex: '1 1 340px' }}>
          <input placeholder="Search name, id or user email…" value={search} onChange={e => setSearch(e.target.value)} />
          <button type="submit" className="secondary">Search</button>
        </form>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 300 }} />}
      {data && (
        <div className="card flush">
          <table className="data">
            <thead>
              <tr><th>Account</th><th>Status</th><th>Plan</th><th className="num">Domains</th><th className="num">Free left</th><th>Joined</th><th></th></tr>
            </thead>
            <tbody>
              {data.data.map((t: any) => (
                <tr key={t.id}>
                  <td>
                    <Link to={`/admin/tenants/${t.id}`} className="strong">{t.name}</Link>
                    <div className="small muted">{t.country || '—'}{t.website ? ` · ${t.website}` : ''}</div>
                  </td>
                  <td><TenantStatus s={t.status} /></td>
                  <td><span className="plan-pill" data-plan={t.plan}>{t.plan}</span></td>
                  <td className="num">{t.verified_domains}/{t.domains}</td>
                  <td className="num">{t.is_free ? t.free_remaining : '—'}</td>
                  <td className="small muted">{relTime(t.created_at)}</td>
                  <td className="num">
                    <Link to={`/admin/tenants/${t.id}?tab=Manage`} className="btn-ghost btn-sm">Manage →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ==================================================================== tenant detail (tabbed)

const TABS = ['Overview', 'Domains', 'Mailbox', 'Sends', 'Manage'] as const
type Tab = typeof TABS[number]

export function AdminTenantDetail() {
  const { id = '' } = useParams()
  const [sp] = useSearchParams()
  const qc = useQueryClient()
  // Honour ?tab=Manage from the tenants-list "Manage →" action so it lands on the right tab.
  const initialTab = (TABS as readonly string[]).includes(sp.get('tab') ?? '') ? (sp.get('tab') as Tab) : 'Overview'
  const [tab, setTab] = useState<Tab>(initialTab)
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'tenant', id], queryFn: () => api.admin.tenant(id) })
  const { data: plans } = useQuery({ queryKey: ['admin', 'plans'], queryFn: api.admin.plans })

  const [plan, setPlan] = useState('')
  const [status, setStatus] = useState('')
  const [free, setFree] = useState('')
  const [reason, setReason] = useState('')
  const [msg, setMsg] = useState<string | null>(null)

  const update = useMutation({
    mutationFn: () => api.admin.updateTenant(id, {
      plan_name: plan || undefined,
      status: status || undefined,
      free_allowance_remaining: free ? Number(free) : undefined,
      reason: reason || undefined,
    }),
    onSuccess: async () => {
      setMsg('Saved.'); setPlan(''); setStatus(''); setFree(''); setReason('')
      await qc.invalidateQueries({ queryKey: ['admin', 'tenant', id] })
      await qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
    onError: (e: any) => setMsg(e.message ?? 'Failed'),
  })

  // One-click lifecycle actions. Suspend/Close stop sending instantly (CanSend => Active|Sandbox
  // only) — the spam kill-switch. Both require a reason (audited); reactivate does not.
  const quick = useMutation({
    mutationFn: (p: { status: string; reason?: string }) => api.admin.updateTenant(id, { status: p.status, reason: p.reason }),
    onSuccess: async () => {
      setMsg('Updated.'); setReason('')
      await qc.invalidateQueries({ queryKey: ['admin', 'tenant', id] })
      await qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
    onError: (e: any) => setMsg(e.message ?? 'Failed'),
  })
  function doQuick(target: string, needReason: boolean) {
    if (needReason && !reason.trim()) { setMsg('Enter a reason in the field below first — required to suspend or close.'); return }
    if (!window.confirm(`${target} this account?${needReason ? `\nReason: ${reason.trim()}` : ''}`)) return
    quick.mutate({ status: target, reason: needReason ? reason.trim() : undefined })
  }

  // Sending-IP pools: assign/unassign the tenant to pools. Assigned pools' IPs are used
  // round-robin for this tenant; with none, it falls back to the default shared pool.
  const poolMut = useMutation({
    mutationFn: (v: { poolId: string; assign: boolean }) =>
      v.assign ? api.admin.assignPool({ tenantId: id, poolId: v.poolId, priority: 100 })
               : api.admin.unassignPool(id, v.poolId),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['admin', 'tenant', id] }) },
    onError: (e: any) => setMsg(e.message ?? 'Failed'),
  })

  if (isLoading) return <div className="skeleton" style={{ height: 360 }} />
  if (!data) return <div className="empty"><p><strong>Tenant not found</strong></p></div>

  return (
    <div>
      <p className="small"><Link to="/admin/tenants">← Tenants</Link></p>
      <div className="admin-head">
        <div>
          <h1 style={{ marginBottom: 8 }}>{data.name}</h1>
          <div className="row" style={{ gap: 8 }}>
            <TenantStatus s={data.status} />
            <span className="plan-pill" data-plan={data.plan}>{data.plan}</span>
            <span className="small muted mono">{data.id}</span>
          </div>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {TABS.map(tb => (
          <button key={tb} role="tab" aria-selected={tab === tb}
                  className={tab === tb ? 'active' : ''} onClick={() => setTab(tb)}>
            {tb}
            {tb === 'Domains' && <span className="tab-count">{data.domains.length}</span>}
            {tb === 'Mailbox' && <span className="tab-count">{data.inbound_messages.length}</span>}
            {tb === 'Sends' && <span className="tab-count">{data.recent_messages.length}</span>}
          </button>
        ))}
      </div>

      {tab === 'Overview' && (
        <>
          <div className="kpis">
            <KpiCard label="Plan" value={data.plan} sub={data.is_free ? `${data.free_remaining} free left` : 'paid'} />
            <KpiCard label="Daily limit" value={data.limits.daily} sub={`${data.limits.hourly}/hour`} />
            <KpiCard label="Domains" value={data.domains.length} sub={`max ${data.limits.max_domains}`} />
            <KpiCard label="First send" value={data.first_send_at ? relTime(data.first_send_at) : 'never'} />
          </div>
          <div className="panel">
            <div className="panel-head"><h2>Usage — recent days</h2></div>
            <StackedBars
              labels={[...data.usage].reverse().map((u: any) => new Date(u.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))}
              series={[
                { key: 'delivered', label: 'Delivered', colour: 'var(--success)', values: [...data.usage].reverse().map((u: any) => u.delivered) },
                { key: 'bounced', label: 'Bounced', colour: 'var(--danger)', values: [...data.usage].reverse().map((u: any) => u.bounced) },
              ]}
            />
          </div>
          <div className="panel">
            <div className="panel-head"><h2>Profile</h2></div>
            <table className="kv">
              <tbody>
                <tr><td>Country</td><td>{data.profile.country || '—'}</td></tr>
                <tr><td>Website</td><td>{data.profile.website || data.profile.Website || '—'}</td></tr>
                <tr><td>Use case</td><td>{data.profile.useCase || data.profile.UseCase || '—'}</td></tr>
                <tr><td>Signup IP</td><td className="mono small">{data.profile.signupIp || data.profile.SignupIp || '—'}</td></tr>
                {data.suspension_reason && <tr><td>Suspension</td><td className="crit">{data.suspension_reason}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'Domains' && (
        <div className="card flush">
          <table className="data">
            <thead><tr><th>Domain</th><th>Status</th><th>Inbound</th><th>Added</th></tr></thead>
            <tbody>
              {data.domains.map((d: any) => (
                <tr key={d.id}><td className="mono">{d.name}</td><td>{d.status}</td><td>{d.inbound ? 'yes' : 'no'}</td><td className="small muted">{relTime(d.created_at)}</td></tr>
              ))}
              {data.domains.length === 0 && <tr><td colSpan={4} className="small muted">No domains.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Mailbox' && (
        <div className="card flush">
          <table className="data">
            <thead><tr><th>From</th><th>Subject</th><th>Type</th><th>Received</th></tr></thead>
            <tbody>
              {data.inbound_messages.map((m: any) => (
                <tr key={m.id}>
                  <td className="mono small">{m.from}</td>
                  <td>{m.subject || '(no subject)'}</td>
                  <td>{m.is_bounce ? <span className="badge pending">bounce</span> : <span className="badge info">mail</span>}</td>
                  <td className="small muted">{relTime(m.received_at)}</td>
                </tr>
              ))}
              {data.inbound_messages.length === 0 && <tr><td colSpan={4} className="small muted">No inbound mail.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Sends' && (
        <div className="card flush">
          <table className="data">
            <thead><tr><th>Subject</th><th>From</th><th className="num">To</th><th>Status</th><th>When</th></tr></thead>
            <tbody>
              {data.recent_messages.map((m: any) => {
                const s = statusBadge(m.status)
                return (
                  <tr key={m.id}>
                    <td>{m.subject || '(no subject)'}</td>
                    <td className="mono small truncate">{m.from}</td>
                    <td className="num">{m.recipients}</td>
                    <td><span className={`badge ${s.cls}`}>{s.text}</span></td>
                    <td className="small muted">{relTime(m.created_at)}</td>
                  </tr>
                )
              })}
              {data.recent_messages.length === 0 && <tr><td colSpan={5} className="small muted">No sends.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Manage' && (
        <>
        <div className="panel" style={{ maxWidth: 560, marginBottom: 16 }}>
          <div className="panel-head"><h2>Quick actions</h2></div>
          <p className="small muted" style={{ marginTop: 0 }}>
            Suspend or close stops all sending immediately — the spam kill-switch. Reason is
            required (it is recorded in the audit log). Reactivate restores sending.
          </p>
          <div className="field">
            <label>Reason <span className="muted">(required to suspend/close)</span></label>
            <input value={reason} onChange={e => setReason(e.target.value)}
                   placeholder="e.g. spam complaints spike, abuse report #123" />
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {data.status !== 'Suspended' && (
              <button className="btn-ghost" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                      disabled={quick.isPending} onClick={() => doQuick('Suspended', true)}>⏸ Suspend (stop sending)</button>
            )}
            {data.status !== 'Active' && (
              <button className="btn-ghost" style={{ color: 'var(--success)', borderColor: 'var(--success)' }}
                      disabled={quick.isPending} onClick={() => doQuick('Active', false)}>▶ Reactivate</button>
            )}
            {data.status !== 'Closed' && (
              <button className="btn-ghost" disabled={quick.isPending}
                      onClick={() => doQuick('Closed', true)}>⛔ Close account</button>
            )}
          </div>
          {msg && <div className="hint" style={{ marginTop: 10 }}>{msg}</div>}
        </div>
        <div className="panel" style={{ maxWidth: 560 }}>
          <div className="panel-head"><h2>Change plan, status & limits</h2></div>
          <div className="field">
            <label>Plan</label>
            <select value={plan} onChange={e => setPlan(e.target.value)}>
              <option value="">— keep {data.plan} —</option>
              {(plans?.data ?? []).map((p: any) => <option key={p.name} value={p.name}>{p.name} ({p.daily.toLocaleString()}/day)</option>)}
            </select>
          </div>
          <div className="field">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">— keep {data.status} —</option>
              <option>Active</option><option>Sandbox</option><option>Suspended</option><option>Closed</option>
            </select>
          </div>
          <div className="field">
            <label>Free allowance remaining</label>
            <input type="number" placeholder={String(data.free_remaining)} value={free} onChange={e => setFree(e.target.value)} />
          </div>
          <div className="field">
            <label>Reason <span className="muted">(required to suspend/close)</span></label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. verified upgrade, abuse report #123" />
          </div>
          <button onClick={() => update.mutate()} disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Apply changes'}</button>
          {msg && <div className="hint" style={{ marginTop: 10 }}>{msg}</div>}
        </div>

        <div className="panel" style={{ maxWidth: 560 }}>
          <div className="panel-head"><h2>Sending IP pools</h2></div>
          <p className="small muted" style={{ marginTop: 0 }}>
            Assign this account to one or more pools. Its mail round-robins across every IP in the
            assigned pools. With none assigned, it uses the default shared pool.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(data.ip_pools ?? []).map((p: any) => (
              <label key={p.id} className="row" style={{ gap: 12, alignItems: 'center', padding: '9px 4px',
                borderBottom: '1px solid var(--border)' }}>
                <input type="checkbox" checked={p.assigned} disabled={poolMut.isPending}
                       onChange={e => poolMut.mutate({ poolId: p.id, assign: e.target.checked })}
                       style={{ width: 17, height: 17 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{p.name}</span>{' '}
                  <span className="badge neutral" style={{ marginLeft: 4 }}>{p.kind}</span>
                  {p.is_default && <span className="small muted"> · default</span>}
                  <div className="small muted">{p.ip_count} IP{p.ip_count === 1 ? '' : 's'} in this pool</div>
                </span>
              </label>
            ))}
            {(data.ip_pools ?? []).length === 0 && <div className="small muted">No pools configured. Create one under Operator → Sending fleet.</div>}
          </div>
        </div>
        </>
      )}
    </div>
  )
}
